import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AxiosError } from 'axios'
import React, { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProposalsApi } from '../../../api/proposals-api'
import GicBreadcrumbs, { BreadcrumbItem } from '../../shared/breadcrumbs'
import { HEADER_HEIGHT_PX } from '../../shared/constants'
import { FeatureFlagEnabled } from '../../shared/feature-flag-enabled'
import {
  CreateProposalDto,
  FileUploadResponse,
  ProposalFile,
  Scenario,
  ScheduleType,
} from '../../shared/types'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import { useIssuers } from '../issuers-context'
import { useProposals } from '../proposal-context'
import ScenariosSection from './scenarios-section'
import UploadDialog from './upload-dialog'
import { validateScenarios } from './validation-utils'
import './create-proposal-page.scss'

interface ValidationErrors {
  fund?: string
  depositAmount?: string
  depositDate?: string
  bidDeadline?: string
  description?: string
  issuerTenantIds?: string
  scenarios?: string
}

// Windsurf generated
const ACCEPTED_FILE_TYPES =
  '.txt, .pdf, .doc, .docx, .xls, .xlsx, .csv, .png, .jpg, .jpeg'

const ACCEPT_ATTRIBUTE = ACCEPTED_FILE_TYPES.replace(/\s/g, '')

function extractErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as Record<string, unknown> | undefined
    if (typeof data?.error === 'string') {
      return data.error
    }
  }
  return err instanceof Error ? err.message : 'Failed to create proposal'
}

export default function CreateProposalPage() {
  const navigate = useNavigate()
  const {
    addProposal,
    uploadState,
    resetUploadState,
    uploadProposalFiles,
    retryFailedUploads,
  } = useProposals()

  // Windsurf generated
  const { flags } = useFeatureFlags()

  // Windsurf generated

  const {
    issuers,
    isLoading: isLoadingIssuers,
    error: issuerLoadError,
    refreshIssuers,
  } = useIssuers()

  // Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
  // Get feature flags to control UI visibility
  const [fund, setFund] = useState<string>('')
  const [depositAmount, setDepositAmount] = useState<string>('')
  const [depositDate, setDepositDate] = useState<Date | null>(null)
  const [bidDeadline, setBidDeadline] = useState<Date | null>(null)
  const [description, setDescription] = useState<string>('')
  const [selectedIssuers, setSelectedIssuers] = useState<string[]>([])
  const [documents, setDocuments] = useState<File[]>([])
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [scenarioValidationErrors, setScenarioValidationErrors] = useState<
    Record<string, string>
  >({})
  const [attemptedSubmit, setAttemptedSubmit] = useState<boolean>(false)

  // Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
  // Default to DEPOSIT schedule type, will be updated from API on mount
  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    ScheduleType.DEPOSIT
  )
  // Track if inputs have been touched to avoid showing validation errors immediately
  const [touched, setTouched] = useState<Record<string, boolean>>({
    fund: false,
    depositAmount: false,
    depositDate: false,
    bidDeadline: false,
    description: false,
    issuers: false,
  })

  // Windsurf generated
  // Ref to the actual input element to read value without dependency
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null)

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Proposals', path: '/proposals' },
    { label: 'Create Proposal' },
  ]

  // Get the user's time zone abbreviation for display
  const getTimeZoneAbbreviation = () => {
    const date = new Date()
    const options: Intl.DateTimeFormatOptions = { timeZoneName: 'short' }
    const timeZoneName =
      new Intl.DateTimeFormat('en-US', options)
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    return timeZoneName
  }

  const timeZoneAbbr = getTimeZoneAbbreviation()

  const parseFormattedNumber = useCallback((value: string): number => {
    return parseFloat(value.replace(/,/g, ''))
  }, [])

  const validateFund = useCallback((value: string): string | undefined => {
    if (value === '') return 'Fund is required'
    return undefined
  }, [])

  const validateDepositAmount = useCallback(
    (value: string): string | undefined => {
      if (value === '') return 'Deposit amount is required'
      const amount = parseFormattedNumber(value)
      if (isNaN(amount) || amount <= 0)
        return 'Deposit amount must be a positive number'
      return undefined
    },
    [parseFormattedNumber]
  )

  const validateDepositDate = useCallback(
    (value: Date | null): string | undefined => {
      if (value == null) return 'Deposit date is required'

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      // Check if date is invalid, year 0000, or in the past
      if (isNaN(value.getTime()) || value.getFullYear() <= 0 || value < today) {
        return 'Deposit date cannot be in the past'
      }

      return undefined
    },
    []
  )

  const validateBidDeadlineWithDepositDate = useCallback(
    (
      bidDeadlineValue: Date | null,
      depositDateValue: Date | null
    ): string | undefined => {
      if (bidDeadlineValue == null) return 'Bid deadline is required'

      // Check if date is invalid, year 0000, or in the past
      if (
        isNaN(bidDeadlineValue.getTime()) ||
        bidDeadlineValue.getFullYear() <= 0 ||
        bidDeadlineValue < new Date()
      ) {
        return 'Bid deadline cannot be in the past'
      }

      if (depositDateValue != null && bidDeadlineValue > depositDateValue)
        return 'Bid deadline must be before deposit date'
      return undefined
    },
    []
  )

  const validateBidDeadline = useCallback(
    (value: Date | null): string | undefined => {
      return validateBidDeadlineWithDepositDate(value, depositDate)
    },
    [depositDate, validateBidDeadlineWithDepositDate]
  )

  const validateDescription = useCallback(
    (value: string): string | undefined => {
      if (value.length > 2000) {
        return 'Description must be 2000 characters or less'
      }
      return undefined
    },
    []
  )

  // Windsurf generated
  // Memoized handler to prevent unnecessary re-renders
  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setDescription(newValue)
      setTouched((prev) => ({ ...prev, description: true }))
      setErrors((prev) => ({
        ...prev,
        description: validateDescription(newValue),
      }))
    },
    [validateDescription]
  )

  const validateIssuerTenantIds = useCallback(
    (value: string[]): string | undefined => {
      if (value.length === 0) return 'At least one issuer must be selected'
      return undefined
    },
    []
  )

  const validateScenariosInForm = useCallback(
    (value: Scenario[]): string | undefined => {
      if (value.length === 0) return 'At least one scenario must be added'
      const depositAmountNum =
        depositAmount !== '' ? parseFormattedNumber(depositAmount) : undefined
      const { isValid, errors } = validateScenarios(
        value,
        scheduleType,
        depositAmountNum,
        flags.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT
      )
      setScenarioValidationErrors(errors)
      return !isValid
        ? 'Please fix the errors in the scenarios below'
        : undefined
    },
    [
      scheduleType,
      depositAmount,
      parseFormattedNumber,
      flags.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT,
    ]
  )

  const handleIssuerChange = (event: SelectChangeEvent<string[]>) => {
    const value = (event.target.value as string[]).filter((id) => {
      if (!flags.ENABLE_ISSUER_STATUS) return true
      const issuer = issuers.find((i) => i.issuerTenantId === id)
      return issuer?.enabled !== false
    })
    setSelectedIssuers(value)
    setTouched({ ...touched, issuers: true })
    setErrors({ ...errors, issuerTenantIds: validateIssuerTenantIds(value) })
  }

  const handleReloadIssuers = async () => {
    await refreshIssuers()
  }

  const handleRemoveIssuer = (issuerToRemove: string) => {
    setSelectedIssuers(
      selectedIssuers.filter((issuer) => issuer !== issuerToRemove)
    )
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files != null) {
      const newFiles = Array.from(event.target.files)
      setDocuments((prev) => [...prev, ...newFiles])
    }
  }

  const handleFileDelete = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index))
  }

  // State for file upload dialog and process
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<ProposalFile[]>([])
  const [isCreatingProposal, setIsCreatingProposal] = useState(false)
  const submitInFlightRef = useRef(false)
  const [creationError, setCreationError] = useState<string | null>(null)
  const [createdProposalId, setCreatedProposalId] = useState<string | null>(
    null
  )

  const handleUploadFiles = useCallback(async () => {
    resetUploadState()
    setUploadError(null)
    setUploadDialogOpen(true)

    try {
      const results = await uploadProposalFiles(documents)

      const successfulFiles = results
        .filter((result: FileUploadResponse) =>
          Boolean(result.success && result.fileId)
        )
        .map((result: FileUploadResponse) => ({
          id: result.fileId as string,
          name: result.fileName,
        }))

      setUploadedFiles(successfulFiles)

      if (results.some((result: FileUploadResponse) => !result.success)) {
        setUploadError(
          'Some files failed to upload. Please retry or remove failed files.'
        )
        return false
      }

      return true
    } catch (err: unknown) {
      setUploadError(
        err instanceof Error ? err.message : 'Failed to upload files'
      )
      return false
    }
  }, [documents, resetUploadState, uploadProposalFiles])

  const handleCreateProposal = useCallback(async () => {
    setIsCreatingProposal(true)
    setCreationError(null)

    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      const proposalDataToSend: CreateProposalDto = {
        fund,
        depositAmount:
          depositAmount !== '' ? parseFormattedNumber(depositAmount) : 0,
        depositDate: depositDate!.toISOString(),
        bidDeadline: bidDeadline!.toISOString(),
        bidDeadlineTimezone: userTimezone,
        scheduleType: scheduleType,
        scenarios: scenarios.map((scenario) => ({
          ...scenario,
          paymentSchedule: scenario.paymentSchedule.map((row) => ({
            payoutAmount: row.payoutAmount,
            payoutPercentage: row.payoutPercentage,
            paymentDate:
              row.paymentDate instanceof Date
                ? row.paymentDate.toISOString()
                : row.paymentDate,
            inputType: row.inputType,
          })),
        })),
        ...(flags.ENABLE_PROPOSAL_DESCRIPTIONS &&
        descriptionInputRef.current?.value != null &&
        descriptionInputRef.current.value !== ''
          ? { description: descriptionInputRef.current.value }
          : {}),
        issuerTenantIds: selectedIssuers,
        files: uploadedFiles,
      }

      // Create the proposal with the uploaded file IDs
      const proposalId = await addProposal(proposalDataToSend)
      setCreatedProposalId(proposalId)
      return proposalId
    } catch (err: unknown) {
      setCreationError(extractErrorMessage(err))
      throw err
    } finally {
      setIsCreatingProposal(false)
    }
    // description read from input ref to avoid recreating callback on every keystroke
  }, [
    fund,
    depositAmount,
    parseFormattedNumber,
    depositDate,
    bidDeadline,
    scheduleType,
    scenarios,
    selectedIssuers,
    addProposal,
    uploadedFiles,
    flags.ENABLE_PROPOSAL_DESCRIPTIONS,
  ])

  // Cancel all uploads and remove any uploaded files
  const handleCancelUploads = useCallback(async () => {
    for (const file of uploadedFiles) {
      try {
        await ProposalsApi.deleteFile(file.id)
      } catch (err: unknown) {
        console.error(`Failed to delete file ${file.name}:`, err)
      }
    }

    // Reset state
    setUploadedFiles([])
    resetUploadState()
    setUploadDialogOpen(false)
  }, [uploadedFiles, resetUploadState])

  const handleRetryFailedUploads = useCallback(async (): Promise<boolean> => {
    try {
      const results = await retryFailedUploads()

      const newFiles: ProposalFile[] = results
        .filter(
          (result) =>
            result.success && result.fileId != null && result.fileId !== ''
        )
        .map((result) => ({
          id: result.fileId as string,
          name: result.fileName,
        }))

      if (newFiles.length > 0) {
        setUploadedFiles((prev) => [...prev, ...newFiles])
      }

      return !results.some((result) => !result.success)
    } catch (err: unknown) {
      console.error('Error retrying uploads:', err)
      return false
    }
  }, [retryFailedUploads, setUploadedFiles])

  const handleSubmit = useCallback(async () => {
    // Drop rapid duplicate clicks before they reach the network.
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    try {
      // Mark all fields as touched for validation
      const allTouched = Object.keys(touched).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      )
      setTouched(allTouched)

      const validateForm = (): boolean => {
        const descriptionValue = descriptionInputRef.current?.value ?? ''
        const newErrors: ValidationErrors = {
          fund: validateFund(fund),
          depositAmount: validateDepositAmount(depositAmount),
          depositDate: validateDepositDate(depositDate),
          bidDeadline: validateBidDeadline(bidDeadline),
          ...(flags.ENABLE_PROPOSAL_DESCRIPTIONS
            ? { description: validateDescription(descriptionValue) }
            : {}),
          issuerTenantIds: validateIssuerTenantIds(selectedIssuers),
          scenarios: validateScenariosInForm(scenarios),
        }

        setErrors(newErrors)
        return !Object.values(newErrors).some((error) => error !== undefined)
      }

      if (!validateForm()) {
        setAttemptedSubmit(true)
        return
      }

      if (documents.length === 0) {
        // Clear any previous file IDs before creating a proposal with no files
        setUploadedFiles([])

        try {
          const proposalId = await handleCreateProposal()
          void navigate(`/proposals/item/${proposalId}`)
        } catch (err: unknown) {
          console.error('Proposal creation error:', err)
        }
        return
      }

      const uploadSuccess = await handleUploadFiles()
      if (!uploadSuccess) return
      // description read from input ref to avoid recreating callback on every keystroke
    } finally {
      submitInFlightRef.current = false
    }
  }, [
    touched,
    setTouched,
    setAttemptedSubmit,
    documents,
    navigate,
    handleUploadFiles,
    handleCreateProposal,
    fund,
    depositAmount,
    depositDate,
    bidDeadline,
    selectedIssuers,
    scenarios,
    setErrors,
    validateFund,
    validateDepositAmount,
    validateDepositDate,
    validateBidDeadline,
    validateDescription,
    validateIssuerTenantIds,
    validateScenariosInForm,
    flags.ENABLE_PROPOSAL_DESCRIPTIONS,
    setUploadedFiles,
  ])

  return (
    <>
      <UploadDialog
        open={uploadDialogOpen}
        uploadState={uploadState}
        uploadError={uploadError}
        creationError={creationError}
        isCreatingProposal={isCreatingProposal}
        createdProposalId={createdProposalId}
        onCancelUploads={handleCancelUploads}
        onRetryFailedUploads={handleRetryFailedUploads}
        onCreateProposal={handleCreateProposal}
        onViewProposal={(proposalId) => {
          void navigate(`/proposals/item/${proposalId}`)
        }}
      />

      <Box
        className="create-proposal-content"
        sx={{
          minHeight: `calc(100vh - ${HEADER_HEIGHT_PX}px)`,
        }}
      >
        <GicBreadcrumbs items={breadcrumbItems} />

        <Typography variant="h5" className="section-title">
          Basic Information
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={3} className="basic-info-grid">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Fund"
                value={fund}
                onChange={(e) => {
                  const newValue = e.target.value
                  setFund(newValue)
                  setTouched({ ...touched, fund: true })
                  setErrors({ ...errors, fund: validateFund(newValue) })
                }}
                size="small"
                required
                error={touched.fund && errors.fund != null}
                helperText={
                  touched.fund && errors.fund != null
                    ? errors.fund
                    : 'Enter the fund name for this proposal *'
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DatePicker
                label="Deposit Date"
                value={depositDate}
                onChange={(newValue) => {
                  setDepositDate(newValue)
                  setTouched({ ...touched, depositDate: true })
                  setErrors({
                    ...errors,
                    depositDate: validateDepositDate(newValue),
                    bidDeadline: validateBidDeadlineWithDepositDate(
                      bidDeadline,
                      newValue
                    ),
                  })
                }}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    fullWidth: true,
                    size: 'small',
                    required: true,
                    placeholder: 'MM/DD/YYYY',
                    helperText:
                      touched.depositDate && errors.depositDate != null
                        ? errors.depositDate
                        : 'When the deposit will be made *',
                    error: touched.depositDate && errors.depositDate != null,
                    InputLabelProps: { shrink: true },
                    InputProps: { notched: true },
                  },
                }}
                shouldDisableDate={(date) => {
                  // Disable past dates
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  return date < today
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Deposit Amount"
                type="text"
                value={depositAmount}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9.]/g, '')

                  // Prevent multiple decimal points
                  const decimalCount = (value.match(/\./g) ?? []).length
                  if (decimalCount > 1) {
                    value = value.substring(0, value.lastIndexOf('.'))
                  }

                  // Limit to 2 decimal places
                  const decimalIndex = value.indexOf('.')
                  if (decimalIndex !== -1 && value.length - decimalIndex > 3) {
                    value = value.substring(0, decimalIndex + 3)
                  }

                  setDepositAmount(value)
                  setTouched({ ...touched, depositAmount: true })
                  setErrors({
                    ...errors,
                    depositAmount: validateDepositAmount(value),
                  })
                }}
                onBlur={(e) => {
                  const value = e.target.value
                  if (value !== '' && !isNaN(parseFloat(value))) {
                    const parts = value.split('.')
                    const integerPart = parseInt(parts[0]).toLocaleString(
                      'en-US'
                    )
                    const formattedValue =
                      parts[1] !== undefined
                        ? `${integerPart}.${parts[1]}`
                        : integerPart
                    setDepositAmount(formattedValue)
                  }
                }}
                size="small"
                required
                error={touched.depositAmount && errors.depositAmount != null}
                helperText={
                  touched.depositAmount && errors.depositAmount != null
                    ? errors.depositAmount
                    : 'Amount to be deposited *'
                }
                slotProps={{
                  input: {
                    inputMode: 'numeric',
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DateTimePicker
                label="Bid Deadline"
                value={bidDeadline}
                onChange={(newValue) => {
                  setBidDeadline(newValue)
                  setTouched({ ...touched, bidDeadline: true })
                  setErrors({
                    ...errors,
                    bidDeadline: validateBidDeadline(newValue),
                  })
                }}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    fullWidth: true,
                    size: 'small',
                    required: true,
                    placeholder: 'MM/DD/YYYY',
                    helperText:
                      touched.bidDeadline && errors.bidDeadline != null
                        ? errors.bidDeadline
                        : `Deadline for bid submissions (${timeZoneAbbr}) *`,
                    error: touched.bidDeadline && errors.bidDeadline != null,
                    InputLabelProps: { shrink: true },
                    InputProps: { notched: true },
                  },
                }}
                shouldDisableDate={(date) => {
                  // Disable past dates and dates after deposit date if it exists
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  const isBeforeToday = date < today
                  const isAfterDepositDate =
                    depositDate != null
                      ? new Date(date) > new Date(depositDate)
                      : false
                  return isBeforeToday || isAfterDepositDate
                }}
                timezone="system"
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* Windsurf generated - Feature flagged proposal description section */}
        <FeatureFlagEnabled flag="ENABLE_PROPOSAL_DESCRIPTIONS">
          <Box className="description-section">
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes for Bidders"
                value={description}
                onChange={handleDescriptionChange}
                multiline
                rows={8}
                size="small"
                error={touched.description && errors.description != null}
                helperText={
                  touched.description && errors.description != null
                    ? errors.description
                    : `These notes will be shared with all selected issuers when the bid request is sent. (${description.length}/2,000 characters)`
                }
                inputProps={{
                  maxLength: 2000,
                }}
                inputRef={descriptionInputRef}
              />
            </Grid>
          </Box>
        </FeatureFlagEnabled>

        <Divider className="divider-spacing" />

        <ScenariosSection
          scenarios={scenarios}
          scheduleType={scheduleType}
          depositAmount={
            depositAmount !== ''
              ? parseFormattedNumber(depositAmount)
              : undefined
          }
          onScheduleTypeChange={setScheduleType}
          onChange={setScenarios}
          error={attemptedSubmit ? errors.scenarios : undefined}
          validationErrors={attemptedSubmit ? scenarioValidationErrors : {}}
          enablePartialMaturities={flags.ENABLE_PARTIAL_MATURITIES}
          enablePartialMaturitiesByDollarAmount={
            flags.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT
          }
        />

        <Divider className="divider-spacing" />

        <Typography variant="h5" className="section-title">
          Select Issuers
        </Typography>

        {issuerLoadError != null && issuerLoadError !== '' && (
          <Box className="issuer-error-box">
            <Typography color="error" variant="body2" gutterBottom>
              {issuerLoadError}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                void handleReloadIssuers()
              }}
            >
              Retry Loading Issuers
            </Button>
          </Box>
        )}

        <FormControl
          fullWidth
          size="small"
          error={touched.issuers && errors.issuerTenantIds != null}
          required
          disabled={isLoadingIssuers}
        >
          <Select
            multiple
            value={selectedIssuers}
            onChange={handleIssuerChange}
            input={<OutlinedInput size="small" />}
            renderValue={(selected) => (
              <Box className="issuer-chips-container">
                {selected.map((value) => {
                  const issuer = issuers.find((i) => i.issuerTenantId === value)
                  const displayName =
                    issuer != null ? issuer.displayName : value

                  return (
                    <Chip
                      key={value}
                      label={displayName}
                      size="small"
                      onDelete={() => handleRemoveIssuer(value)}
                      onMouseDown={(event) => {
                        event.stopPropagation()
                      }}
                    />
                  )
                })}
              </Box>
            )}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 224,
                  width: 250,
                },
              },
            }}
          >
            {isLoadingIssuers ? (
              <MenuItem disabled>
                <CircularProgress size={20} className="loading-spinner" />
                <ListItemText primary="Loading issuers..." />
              </MenuItem>
            ) : !Array.isArray(issuers) || issuers.length === 0 ? (
              <MenuItem disabled>
                <ListItemText primary="No issuers available" />
              </MenuItem>
            ) : (
              [...issuers]
                .sort((a, b) =>
                  a.displayName.localeCompare(b.displayName, undefined, {
                    sensitivity: 'base',
                  })
                )
                .map((issuer) => (
                  <MenuItem
                    key={issuer.issuerTenantId}
                    value={issuer.issuerTenantId}
                    disabled={flags.ENABLE_ISSUER_STATUS && !issuer.enabled}
                    sx={
                      flags.ENABLE_ISSUER_STATUS && !issuer.enabled
                        ? { '&.Mui-disabled': { opacity: 1 } }
                        : undefined
                    }
                  >
                    <Checkbox
                      checked={
                        selectedIssuers.indexOf(issuer.issuerTenantId) > -1
                      }
                      disabled={flags.ENABLE_ISSUER_STATUS && !issuer.enabled}
                    />
                    <ListItemText
                      primary={issuer.displayName}
                      sx={
                        flags.ENABLE_ISSUER_STATUS && !issuer.enabled
                          ? { opacity: 0.38, flex: '0 1 auto' }
                          : undefined
                      }
                    />
                    {flags.ENABLE_ISSUER_STATUS && !issuer.enabled && (
                      <Chip
                        icon={<InfoOutlinedIcon />}
                        label="Issuer has not yet onboarded with GIC Marketplace."
                        size="small"
                        variant="outlined"
                        sx={{
                          ml: 1,
                          color: '#0288d1',
                          borderColor: '#0288d1',
                          '& .MuiChip-icon': { color: '#0288d1' },
                        }}
                      />
                    )}
                  </MenuItem>
                ))
            )}
          </Select>
          <FormHelperText>
            {touched.issuers && errors.issuerTenantIds != null
              ? errors.issuerTenantIds
              : isLoadingIssuers
                ? 'Loading issuers...'
                : 'Select issuers for this proposal *'}
          </FormHelperText>
        </FormControl>

        <Divider className="divider-spacing" />

        <Typography variant="h5" className="section-title-with-spacing">
          Attach any relevant documents for this proposal
          <Tooltip
            title={
              <>
                <Typography variant="subtitle2" gutterBottom>
                  File Requirements:
                </Typography>
                <Typography variant="body2">
                  • Maximum file size: 50MB
                </Typography>
                <Typography variant="body2">
                  • Allowed characters: A-Z, a-z, 0-9, !, -, _, ., *, ', (, )
                </Typography>
                <Typography variant="body2">
                  • Allowed types: {ACCEPTED_FILE_TYPES}
                </Typography>
              </>
            }
            placement="right"
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  boxShadow: 'none',
                  '& .MuiTooltip-arrow': {
                    color: 'primary.light',
                  },
                },
              },
            }}
          >
            <ErrorOutlineIcon fontSize="small" className="error-tooltip-icon" />
          </Tooltip>
        </Typography>

        <Box className="documents-container">
          <Button
            variant="outlined"
            component="label"
            size="small"
            className="upload-button"
          >
            Attach Documents
            <input
              type="file"
              hidden
              multiple
              accept={ACCEPT_ATTRIBUTE}
              onChange={handleFileUpload}
            />
          </Button>
        </Box>

        {documents.length > 0 && (
          <Box className="uploaded-documents-section">
            <Typography variant="subtitle1" gutterBottom>
              Uploaded Documents:
            </Typography>
            <Grid container spacing={1}>
              {documents.map((file, index) => (
                <Grid key={`${file.name}-${file.size}-${file.lastModified}`}>
                  <Chip
                    label={file.name}
                    onDelete={() => handleFileDelete(index)}
                    size="small"
                    className="document-chip"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box className="submit-actions-container">
          {attemptedSubmit &&
            Object.values(errors).some((error) => error !== undefined) && (
              <Tooltip title="Please fix the highlighted errors before submitting">
                <Box className="form-error-indicator">
                  <ErrorOutlineIcon
                    data-testid="ErrorOutlineIcon"
                    fontSize="small"
                  />
                  <Typography variant="body2" color="error">
                    Form has errors
                  </Typography>
                </Box>
              </Tooltip>
            )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={uploadState.uploading || isCreatingProposal}
            className="submit-button"
          >
            Upload Documents and Send Proposal
          </Button>
        </Box>
      </Box>
    </>
  )
}
